import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Alert, Divider, Tooltip, Checkbox, Snackbar, Accordion,
  AccordionSummary, AccordionDetails,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ApiIcon from '@mui/icons-material/Api';
import StarIcon from '@mui/icons-material/Star';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import db from '../../data/demoDatabase';
import { apiProductCatalog, PRODUCT_CATEGORIES } from '../../data/productConfigData';
import { useAuth } from '../../context/AuthContext';
import StatCard from '../../components/common/StatCard';
import DataTable from '../../components/common/DataTable';

const PRIMARY = '#1B365D';

const statusColors = {
  active: { bg: '#E8F5E9', color: '#2E7D32', label: 'Active' },
  expiring_soon: { bg: '#FFF8E1', color: '#F57F17', label: 'Expiring Soon' },
  expired: { bg: '#FFEBEE', color: '#C62828', label: 'Expired' },
  pending_l1: { bg: '#E3F2FD', color: '#1565C0', label: 'Pending L1' },
  pending_l2: { bg: '#EDE7F6', color: '#4527A0', label: 'Pending L2' },
  rejected: { bg: '#FFEBEE', color: '#C62828', label: 'Rejected' },
};

// New trigger-per-tier shape: each tier has { triggerStatus, tokens }
const blankRules = {
  selfApplication: { triggerStatus: 'disbursed', tokens: 0 },
  referralTiers: {
    level1: { triggerStatus: 'approved', tokens: 0 },
    level2: { triggerStatus: 'approved', tokens: 0 },
    level3: { triggerStatus: 'approved', tokens: 0 },
  },
  registrationBonus: { level1: 0, level2: 0, level3: 0 },
  taskBonus: [],
};

const TRIGGER_OPTIONS = [
  { key: 'submitted', label: 'On Submitted' },
  { key: 'approved', label: 'On Approved' },
  { key: 'disbursed', label: 'On Disbursed' },
];

const DEFAULT_TASK_PRESETS = [
  { key: 'social_share', label: 'Share referral link to social media', tokens: 5, oneTime: true },
  { key: 'google_review', label: 'Leave a Google review', tokens: 2, oneTime: true },
  { key: 'ekyc_completed', label: 'Complete eKYC verification', tokens: 10, oneTime: true },
];

export default function RewardConfig() {
  const { user } = useAuth();
  const [categoryTab, setCategoryTab] = useState(0);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [refreshKey, setRefreshKey] = useState(0);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);

  const bump = () => setRefreshKey((k) => k + 1);
  const showSnack = (msg, severity = 'success') =>
    setSnack({ open: true, msg, severity });

  const allConfigs = useMemo(() => db.rewardConfigs.getAll(), [refreshKey]);
  const auditLogs = useMemo(
    () => db.auditLogs.getAll().filter((l) => l.entityType === 'reward_config'),
    [refreshKey]
  );

  const stats = useMemo(() => {
    const active = allConfigs.filter((c) => c.computedStatus === 'active').length;
    const expiring = allConfigs.filter((c) => {
      if (c.computedStatus !== 'active' || !c.expiryDate) return false;
      const days = Math.floor((new Date(c.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
      return days >= 0 && days <= 30;
    }).length;
    const pending = allConfigs.filter((c) =>
      c.computedStatus === 'pending_l1' || c.computedStatus === 'pending_l2'
    ).length;
    const expired = allConfigs.filter((c) => c.computedStatus === 'expired').length;
    return { active, expiring, pending, expired };
  }, [allConfigs]);

  const writeAuditLog = (action, entityId, description, reason = '') => {
    db.auditLogs.create({
      userId: user?.id || 'SYSTEM',
      userName: user?.name || 'System',
      userRole: user?.role || 'admin',
      action,
      entityType: 'reward_config',
      entityId,
      description,
      reason,
    });
  };

  // ─── Import Dialog ──────────────────────────────────────
  const ImportDialog = () => {
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [reason, setReason] = useState('');
    const [expiryMonths, setExpiryMonths] = useState(12);

    const existingCodes = allConfigs.map((c) => c.productCode);
    const availableProducts = apiProductCatalog.filter(
      (p) => !existingCodes.includes(p.productCode)
    );

    const toggleSelect = (code) => {
      setSelectedCodes((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
      );
    };

    const handleImport = () => {
      if (selectedCodes.length === 0) {
        showSnack('Please select at least one product', 'warning');
        return;
      }
      const now = new Date();
      const expire = new Date(now);
      expire.setMonth(expire.getMonth() + expiryMonths);

      selectedCodes.forEach((code) => {
        const apiProduct = apiProductCatalog.find((p) => p.productCode === code);
        if (!apiProduct) return;
        const created = db.rewardConfigs.create({
          productCode: apiProduct.productCode,
          category: apiProduct.category,
          lenderName: apiProduct.lenderName,
          productName: apiProduct.productName,
          effectiveFrom: now.toISOString().slice(0, 10),
          expiryDate: expire.toISOString().slice(0, 10),
          ...blankRules,
          createdBy: user?.id || 'SYSTEM',
        });
        writeAuditLog(
          'create_reward_config', created.id,
          `Imported reward config for ${apiProduct.productName}`, reason
        );
      });

      showSnack(`${selectedCodes.length} reward config(s) imported and submitted for approval`);
      setImportDialogOpen(false);
      setSelectedCodes([]);
      setReason('');
      bump();
    };

    return (
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ApiIcon sx={{ color: '#1565C0' }} />
          Fetch from imoney.my Product API
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Select products to configure reward rules for. Each new reward config enters the 2-layer approval workflow.
          </Alert>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField fullWidth select label="Token Expiry" size="small"
                value={expiryMonths} onChange={(e) => setExpiryMonths(Number(e.target.value))}>
                <MenuItem value={6}>6 Months</MenuItem>
                <MenuItem value={12}>1 Year</MenuItem>
                <MenuItem value={24}>2 Years</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Reason / Justification"
                value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Q2 reward refresh" />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            Available Products ({availableProducts.length})
          </Typography>
          {availableProducts.length === 0 ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              All products in the API catalog already have reward configs.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedCodes.length === availableProducts.length && availableProducts.length > 0}
                        indeterminate={selectedCodes.length > 0 && selectedCodes.length < availableProducts.length}
                        onChange={(e) =>
                          setSelectedCodes(e.target.checked ? availableProducts.map((p) => p.productCode) : [])
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Product Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Lender</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Product Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availableProducts.map((p) => {
                    const cat = PRODUCT_CATEGORIES.find((c) => c.key === p.category);
                    return (
                      <TableRow key={p.productCode} hover onClick={() => toggleSelect(p.productCode)} sx={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedCodes.includes(p.productCode)} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{p.productCode}</TableCell>
                        <TableCell>
                          <Chip label={cat?.label || p.category} size="small"
                            sx={{ backgroundColor: `${cat?.color}15`, color: cat?.color, fontWeight: 600, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{p.lenderName}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{p.productName}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setImportDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleImport} disabled={selectedCodes.length === 0}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: PRIMARY }}>
            Import {selectedCodes.length > 0 ? `(${selectedCodes.length})` : ''} & Submit for Approval
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ─── Edit Dialog ────────────────────────────────────────
  const EditDialog = () => {
    const [rules, setRules] = useState(blankRules);
    const [reason, setReason] = useState('');
    const [expiryDate, setExpiryDate] = useState('');

    React.useEffect(() => {
      if (selectedConfig && editDialogOpen) {
        // Migrate legacy shape if needed
        const migrateLegacyTier = (tier, defaultTrigger) => {
          if (!tier) return { triggerStatus: defaultTrigger, tokens: 0 };
          if (typeof tier.triggerStatus === 'string') return tier;
          // Legacy shape — pick the highest non-zero value
          const candidates = ['submitted', 'approved', 'disbursed']
            .map((k) => ({ status: k, value: Number(tier[k]) || 0 }))
            .filter((c) => c.value > 0)
            .sort((a, b) => b.value - a.value);
          if (candidates.length === 0) return { triggerStatus: defaultTrigger, tokens: 0 };
          return { triggerStatus: candidates[0].status, tokens: candidates[0].value };
        };

        setRules({
          selfApplication: migrateLegacyTier(selectedConfig.selfApplication, 'disbursed'),
          referralTiers: {
            level1: migrateLegacyTier(selectedConfig.referralTiers?.level1, 'approved'),
            level2: migrateLegacyTier(selectedConfig.referralTiers?.level2, 'approved'),
            level3: migrateLegacyTier(selectedConfig.referralTiers?.level3, 'approved'),
          },
          registrationBonus: selectedConfig.registrationBonus || blankRules.registrationBonus,
          taskBonus: Array.isArray(selectedConfig.taskBonus) ? selectedConfig.taskBonus : [],
        });
        setExpiryDate(selectedConfig.expiryDate || '');
        setReason('');
      }
    }, [selectedConfig, editDialogOpen]);

    const updateSelf = (field, value) => {
      setRules((prev) => ({
        ...prev,
        selfApplication: {
          ...prev.selfApplication,
          [field]: field === 'tokens' ? (Number(value) || 0) : value,
        },
      }));
    };

    const updateTier = (tier, field, value) => {
      setRules((prev) => ({
        ...prev,
        referralTiers: {
          ...prev.referralTiers,
          [tier]: {
            ...prev.referralTiers[tier],
            [field]: field === 'tokens' ? (Number(value) || 0) : value,
          },
        },
      }));
    };

    const updateBonus = (tier, value) => {
      setRules((prev) => ({
        ...prev,
        registrationBonus: { ...prev.registrationBonus, [tier]: Number(value) || 0 },
      }));
    };

    const addTask = (preset = null) => {
      const newTask = preset || { key: `task_${Date.now()}`, label: '', tokens: 0, oneTime: true };
      setRules((prev) => ({ ...prev, taskBonus: [...(prev.taskBonus || []), newTask] }));
    };

    const updateTask = (idx, field, value) => {
      setRules((prev) => ({
        ...prev,
        taskBonus: prev.taskBonus.map((t, i) =>
          i === idx ? { ...t, [field]: field === 'tokens' ? (Number(value) || 0) : value } : t
        ),
      }));
    };

    const removeTask = (idx) => {
      setRules((prev) => ({
        ...prev,
        taskBonus: prev.taskBonus.filter((_, i) => i !== idx),
      }));
    };

    const handleSave = () => {
      if (!selectedConfig) return;
      db.rewardConfigs.update(selectedConfig.id, {
        ...rules,
        expiryDate: expiryDate || selectedConfig.expiryDate,
        approvalStatus: 'pending_l1',
        l1ApprovedBy: null, l1ApprovedAt: null,
        l2ApprovedBy: null, l2ApprovedAt: null,
      });
      writeAuditLog(
        'update_reward_config', selectedConfig.id,
        `Updated reward rules for ${selectedConfig.productName}`, reason
      );
      showSnack('Changes submitted for L1 approval');
      setEditDialogOpen(false);
      bump();
    };

    if (!selectedConfig) return null;

    const renderTriggerRow = (label, tier, updateFn) => (
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={7}>
          <TextField
            fullWidth size="small" select
            label={`${label} — Trigger Status`}
            value={tier.triggerStatus || 'disbursed'}
            onChange={(e) => updateFn('triggerStatus', e.target.value)}
          >
            {TRIGGER_OPTIONS.map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth size="small" type="number" label="Tokens"
            value={tier.tokens}
            onChange={(e) => updateFn('tokens', e.target.value)}
          />
        </Grid>
      </Grid>
    );

    return (
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Edit Reward Rules</Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            {selectedConfig.productName} ({selectedConfig.productCode})
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Each tier fires tokens at ONE trigger status. Choose when the tokens are credited
            (e.g., "On Disbursed" means users only earn when the case is fully disbursed).
          </Alert>

          {/* Self Application */}
          <Accordion defaultExpanded sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY }}>
                Self-Application Reward (user applies for themselves)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {renderTriggerRow('Self', rules.selfApplication, (field, value) => updateSelf(field, value))}
            </AccordionDetails>
          </Accordion>

          {/* 3-level referral tiers */}
          {[
            { key: 'level1', label: 'Level 1 — Direct Referral' },
            { key: 'level2', label: 'Level 2 — Downline of Direct Referral' },
            { key: 'level3', label: 'Level 3 — Three Levels Deep' },
          ].map(({ key, label }) => (
            <Accordion key={key} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY }}>
                  {label}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderTriggerRow(label, rules.referralTiers[key], (field, value) => updateTier(key, field, value))}
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Registration Bonus */}
          <Accordion sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY }}>
                Registration Bonus (when a new user signs up via your referral link)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" type="number" label="L1 Tokens"
                    value={rules.registrationBonus.level1}
                    onChange={(e) => updateBonus('level1', e.target.value)} />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" type="number" label="L2 Tokens"
                    value={rules.registrationBonus.level2}
                    onChange={(e) => updateBonus('level2', e.target.value)} />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" type="number" label="L3 Tokens"
                    value={rules.registrationBonus.level3}
                    onChange={(e) => updateBonus('level3', e.target.value)} />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Task Bonus */}
          <Accordion defaultExpanded sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY }}>
                Task Bonus ({(rules.taskBonus || []).length}) — one-time rewards per user
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {(rules.taskBonus || []).length === 0 && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  No tasks configured. Add a preset or create a custom task below.
                </Alert>
              )}
              {(rules.taskBonus || []).map((task, idx) => (
                <Grid container spacing={1} key={idx} sx={{ mb: 1, alignItems: 'center' }}>
                  <Grid item xs={12} sm={5}>
                    <TextField fullWidth size="small" label="Task Label"
                      value={task.label || ''}
                      onChange={(e) => updateTask(idx, 'label', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth size="small" label="Task Key (id)"
                      value={task.key || ''}
                      onChange={(e) => updateTask(idx, 'key', e.target.value)}
                      helperText="Unique identifier"
                      FormHelperTextProps={{ sx: { fontSize: '0.65rem', m: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={9} sm={3}>
                    <TextField fullWidth size="small" type="number" label="Tokens"
                      value={task.tokens || 0}
                      onChange={(e) => updateTask(idx, 'tokens', e.target.value)} />
                  </Grid>
                  <Grid item xs={3} sm={1}>
                    <IconButton size="small" color="error" onClick={() => removeTask(idx)}>
                      <CancelIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" onClick={() => addTask()}
                  sx={{ borderRadius: 2, textTransform: 'none' }}>
                  + Add Custom Task
                </Button>
                {DEFAULT_TASK_PRESETS.map((preset) => {
                  const already = (rules.taskBonus || []).some((t) => t.key === preset.key);
                  if (already) return null;
                  return (
                    <Button
                      key={preset.key}
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={() => addTask(preset)}
                      sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                    >
                      + {preset.label}
                    </Button>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Token Expiry Date"
                value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Tokens earned from this config expire on this date" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Reason / Justification"
                value={reason} onChange={(e) => setReason(e.target.value)}
                helperText="Required for audit log" />
            </Grid>
          </Grid>

          <Alert severity="warning" sx={{ borderRadius: 2, mt: 2 }}>
            Changes will be submitted for 2-layer manager approval. Token rules only take effect after both approvals.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: PRIMARY }}>
            Submit for Approval
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ─── Audit Dialog ───────────────────────────────────────
  const AuditDialog = () => {
    if (!selectedConfig) return null;
    const logs = db.auditLogs.getByEntity('reward_config', selectedConfig.id);
    return (
      <Dialog open={auditDialogOpen} onClose={() => setAuditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Audit History</Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            {selectedConfig.productName}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {logs.length === 0 ? (
            <Alert severity="info">No audit entries for this reward config yet.</Alert>
          ) : (
            logs.map((log) => (
              <Box key={log.id} sx={{ mb: 2, p: 2, border: '1px solid #E0E0E0', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Chip label={log.action} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(log.timestamp).toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{log.description}</Typography>
                <Typography variant="caption" color="text.secondary">
                  by {log.userName} ({log.userRole})
                </Typography>
                {log.reason && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    Reason: {log.reason}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ─── Reward Config Card ─────────────────────────────────
  const ConfigCard = ({ config }) => {
    const status = statusColors[config.computedStatus] || statusColors.active;
    const daysLeft = config.expiryDate
      ? Math.floor((new Date(config.expiryDate) - new Date()) / (1000 * 60 * 60 * 24))
      : null;

    return (
      <Card sx={{
        borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }, transition: 'all 0.2s',
      }}>
        <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Box>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#90A4AE' }}>
                {config.productCode}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY, lineHeight: 1.2 }}>
                {config.lenderName}
              </Typography>
              <Typography variant="caption" color="text.secondary">{config.productName}</Typography>
            </Box>
            <Chip label={status.label} size="small"
              sx={{ backgroundColor: status.bg, color: status.color, fontWeight: 700, fontSize: '0.65rem' }} />
          </Box>
          <Divider sx={{ my: 1 }} />

          {/* Helper: describe a tier in "X tokens on <status>" format */}
          {(() => {
            const describeTier = (tier) => {
              if (!tier) return '0';
              if (typeof tier.triggerStatus === 'string') {
                return `${tier.tokens || 0} on ${tier.triggerStatus}`;
              }
              // Legacy fallback
              const parts = ['submitted', 'approved', 'disbursed']
                .filter((k) => Number(tier[k]) > 0);
              if (parts.length === 0) return '0';
              return parts.map((k) => `${tier[k]} on ${k}`).join(', ');
            };
            return (
              <>
                {/* Self application summary */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: PRIMARY, display: 'block', mb: 0.25 }}>
                    SELF-APPLICATION
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                    {describeTier(config.selfApplication)}
                  </Typography>
                </Box>

                {/* 3-level referrals */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: PRIMARY, display: 'block', mb: 0.25 }}>
                    REFERRAL TIERS
                  </Typography>
                  {['level1', 'level2', 'level3'].map((tier, idx) => (
                    <Box key={tier} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                      <Typography variant="caption" sx={{ color: '#7B1FA2', fontWeight: 600 }}>L{idx + 1}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {describeTier(config.referralTiers?.[tier])}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            );
          })()}

          {/* Registration bonus */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: PRIMARY, display: 'block', mb: 0.25 }}>
              REGISTRATION BONUS
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              L1: {config.registrationBonus?.level1 || 0} · L2: {config.registrationBonus?.level2 || 0} · L3: {config.registrationBonus?.level3 || 0}
            </Typography>
          </Box>

          {/* Task bonus count */}
          {Array.isArray(config.taskBonus) && config.taskBonus.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: PRIMARY, display: 'block', mb: 0.25 }}>
                TASK BONUS ({config.taskBonus.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {config.taskBonus.slice(0, 2).map((t) => t.label).join(' · ')}
                {config.taskBonus.length > 2 && ` +${config.taskBonus.length - 2} more`}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Expires</Typography>
            <Typography variant="caption" sx={{
              fontWeight: 600,
              color: daysLeft !== null && daysLeft < 0 ? '#C62828' : daysLeft !== null && daysLeft <= 30 ? '#F57F17' : '#2E7D32',
            }}>
              {config.expiryDate || '—'} {daysLeft !== null && daysLeft >= 0 ? `(${daysLeft}d)` : daysLeft !== null ? '(expired)' : ''}
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit Rules">
              <IconButton size="small" onClick={() => { setSelectedConfig(config); setEditDialogOpen(true); }}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Audit History">
              <IconButton size="small" onClick={() => { setSelectedConfig(config); setAuditDialogOpen(true); }}>
                <HistoryIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ─── Category Tab Content ───────────────────────────────
  const renderCategoryContent = (categoryKey) => {
    const configs = allConfigs.filter((c) => c.category === categoryKey);
    if (configs.length === 0) {
      return (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No reward configs in this category. Click "Fetch from imoney.my API" to import.
        </Alert>
      );
    }
    return (
      <Grid container spacing={2.5}>
        {configs.map((config) => (
          <Grid item xs={12} sm={6} md={4} key={config.id}>
            <ConfigCard config={config} />
          </Grid>
        ))}
      </Grid>
    );
  };

  // ─── Approval Queue ─────────────────────────────────────
  const renderApprovalQueue = () => {
    const pending = allConfigs.filter(
      (c) => c.computedStatus === 'pending_l1' || c.computedStatus === 'pending_l2'
    );
    if (pending.length === 0) {
      return <Alert severity="success" sx={{ borderRadius: 2 }}>No pending reward config approvals.</Alert>;
    }
    return (
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F5F7FA' }}>
              <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Stage</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Submitted By</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pending.map((config) => {
              const cat = PRODUCT_CATEGORIES.find((c) => c.key === config.category);
              return (
                <TableRow key={config.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{config.lenderName}</Typography>
                    <Typography variant="caption" color="text.secondary">{config.productName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={cat?.label} size="small"
                      sx={{ backgroundColor: `${cat?.color}15`, color: cat?.color, fontWeight: 600, fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={config.computedStatus === 'pending_l1' ? 'Stage 1/2' : 'Stage 2/2'} size="small"
                      sx={{
                        backgroundColor: config.computedStatus === 'pending_l1' ? '#E3F2FD' : '#EDE7F6',
                        color: config.computedStatus === 'pending_l1' ? '#1565C0' : '#4527A0',
                        fontWeight: 700, fontSize: '0.7rem',
                      }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{config.createdBy}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {config.computedStatus === 'pending_l1' && (
                        <Button size="small" variant="contained" startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                          sx={{ backgroundColor: '#00B894', borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                          onClick={() => {
                            db.rewardConfigs.approveL1(config.id, user?.id || 'SYSTEM');
                            writeAuditLog('approve_l1', config.id, `L1 approval granted for ${config.productName}`);
                            showSnack('L1 approval granted');
                            bump();
                          }}>
                          Approve L1
                        </Button>
                      )}
                      {config.computedStatus === 'pending_l2' && (
                        <Button size="small" variant="contained" startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                          sx={{ backgroundColor: PRIMARY, borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                          onClick={() => {
                            db.rewardConfigs.approveL2(config.id, user?.id || 'SYSTEM');
                            writeAuditLog('approve_l2', config.id, `L2 approval granted for ${config.productName} — now active`);
                            showSnack('L2 approval granted — reward config now active');
                            bump();
                          }}>
                          Approve L2
                        </Button>
                      )}
                      <Button size="small" variant="outlined" color="error" startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                        sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                        onClick={() => {
                          const reason = window.prompt('Rejection reason?');
                          if (reason) {
                            db.rewardConfigs.reject(config.id, reason);
                            writeAuditLog('reject', config.id, `Rejected ${config.productName}`, reason);
                            showSnack('Reward config rejected', 'warning');
                            bump();
                          }
                        }}>
                        Reject
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ─── Audit Log ──────────────────────────────────────────
  const renderAuditLog = () => {
    const columns = [
      { field: 'timestamp', headerName: 'Timestamp', renderCell: (row) => (
        <Typography variant="caption">{new Date(row.timestamp).toLocaleString()}</Typography>
      )},
      { field: 'action', headerName: 'Action', renderCell: (row) => (
        <Chip label={row.action} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
      )},
      { field: 'entityId', headerName: 'Entity' },
      { field: 'userName', headerName: 'User' },
      { field: 'description', headerName: 'Description' },
      { field: 'reason', headerName: 'Reason' },
    ];
    return <DataTable columns={columns} rows={auditLogs} title="Reward Config Audit Log" exportable pageSize={15} />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>
            Reward Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Per-product token rules with 3-level referral structure and 2-layer approval
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<ApiIcon />} onClick={() => setImportDialogOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, backgroundColor: PRIMARY }}>
            Fetch from imoney.my API
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={bump}
            sx={{ borderRadius: 2, textTransform: 'none' }}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard title="Active Configs" value={stats.active} subtitle="currently live"
            icon={CheckCircleIcon} color="#00B894" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Expiring Soon" value={stats.expiring} subtitle="within 30 days"
            icon={WarningIcon} color="#F57F17" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Pending Approval" value={stats.pending} subtitle="awaiting review"
            icon={ScheduleIcon} color="#1565C0" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Expired" value={stats.expired} subtitle="contract lapsed"
            icon={CancelIcon} color="#C62828" />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
        <Tabs value={categoryTab} onChange={(_, v) => setCategoryTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: '1px solid #E0E0E0', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
          {PRODUCT_CATEGORIES.map((cat) => (
            <Tab key={cat.key} label={cat.label} />
          ))}
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Approval Queue
              {stats.pending > 0 && (
                <Chip label={stats.pending} size="small"
                  sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#C62828', color: 'white', fontWeight: 700 }} />
              )}
            </Box>
          } />
          <Tab label="Audit Log" />
        </Tabs>
        <Box sx={{ p: 3 }}>
          {categoryTab < PRODUCT_CATEGORIES.length && renderCategoryContent(PRODUCT_CATEGORIES[categoryTab].key)}
          {categoryTab === PRODUCT_CATEGORIES.length && renderApprovalQueue()}
          {categoryTab === PRODUCT_CATEGORIES.length + 1 && renderAuditLog()}
        </Box>
      </Paper>

      {/* Dialogs */}
      <ImportDialog />
      <EditDialog />
      <AuditDialog />

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}
          sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
